import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { Component } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from 'react-bootstrap';
import { Project } from '../../../../types';
import Icon, { icons } from '../../../generic/Icon';
import { createComparator, SortOptions } from '../helper';
import ProjectListRow from './ProjectListRow';
import SortingHeaderItem from './SortingHeader';

const PAGE_SIZE = 10;

type ProjectTableProps = {
  projects: Project[];
  loading?: boolean;
  showHeader?: boolean;
  onProjectSelected: (project: Project) => void;
};

type ProjectTableState = {
  search: string;
  page: number;
  sort: SortOptions;
}

export default class ProjectGroupListTable extends Component<ProjectTableProps, ProjectTableState> {
  state = {
    search: '',
    page: 0,
    sort: { key: 'title' as keyof Project, asc: true },
  };

  componentDidUpdate(prevProps: ProjectTableProps) {
    if (prevProps.projects.length > this.props.projects.length) {
      if (this.state.page > 0 && (this.props.projects.length / PAGE_SIZE) <= this.state.page) {
        // This rule is to prevent runaway rerenders, we're being careful here.
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState(prevState => ({ page: prevState.page - 1 }));
      }
    }
  }

  onSortChange = (key: keyof Project, asc: boolean) => {
    this.setState({ sort: { key, asc }});
  }

  onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: e.target.value, page: 0 });
  }

  onPageUp = () => {
    const maxPage = Math.floor(this.props.projects.length / PAGE_SIZE);
    if (this.state.page < maxPage) {
      this.setState((state => ({ page: state.page + 1 })));
    }
  };

  onPageDown = () => {
    if (this.state.page > 0) {
      this.setState((state => ({ page: state.page - 1 })));
    }
  };

  getProjectValue = (project: Project, key: string) => {
    if (key === 'revision') {
      return (project.revisions?.[0].dependency_count ?? 0) + (project.revisions?.[0].license_count ?? 0);
    }
    return get(project, key);
  }

  filteredProjects = () => (
    this.state.search ? (
      this.props.projects.filter(row => toLower(row.title).includes(toLower(this.state.search)))
    ) : (
      this.props.projects
    )
  );

  displayedProjectIndexes = () => {
    const projects = this.filteredProjects();
    if (projects.length < PAGE_SIZE) {
      return [0, projects.length - 1];
    }

    const page = this.state.page;
    const min = page * PAGE_SIZE;
    let max = (page + 1) * PAGE_SIZE;
    if (max >= projects.length) {
      max = projects.length;
    }

    return [min, max - 1];
  }

  sortedFilteredProjects = () => {
    const projects = this.filteredProjects();

    const comparator = createComparator(this.state.sort, this.getProjectValue);
    return projects.sort(comparator);
  }

  render() {
    const { loading, showHeader, onProjectSelected } = this.props;
    const [indexMin, indexMax] = this.displayedProjectIndexes();
    const allProjects = this.sortedFilteredProjects();
    const projects = allProjects.slice(indexMin, indexMax + 1);

    return (
      <div className="project-table">
        {
          showHeader && (
            <div className="panel panel-search">
              <div className="panel-body padded padded-small">
                <div className="row">
                  <div className="col-xs-7 search-header">
                    <span className="input-search-wrapper">
                      <input
                        type="text"
                        className="form-control input-search"
                        placeholder="Search by title..."
                        onChange={this.onSearchChange}
                        value={this.state.search}
                      />
                    </span>
                  </div>
                  <div className="col-xs-5 text-right subtitle">
                    {projects.length === 0 ? (
                      <b>0</b>
                    ) : (
                      <b>{indexMin + 1} - {indexMax + 1}</b>
                    )}{' '}
                    of <b>{allProjects.length}</b> projects &nbsp;&nbsp;&nbsp;
                    <Button bsStyle="link" disabled={indexMin <= 0} onClick={this.onPageDown}>
                      <Icon icon={icons.caretLeft} />
                    </Button>{' '}
                    {'  '}
                    <Button
                      bsStyle="link"
                      disabled={(indexMax + 1) === allProjects.length}
                      onClick={this.onPageUp}
                    >
                      <Icon icon={icons.caretRight} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        <div className="panel panel-select">
          <div className="panel-body nopadding">
            <table className="table nomargin-v table-fill">
              <thead>
                <tr>
                  <th style={{width: '5%'}}>{' '}</th>
                  <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="title">
                    Title
                  </SortingHeaderItem>
                  <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="revisions">
                    Stats
                  </SortingHeaderItem>
                  <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="updatedAt">
                    Last Updated
                  </SortingHeaderItem>
                </tr>
              </thead>
              <tbody className={loading ? 'loading loading-cruncher' : ''}>
                {
                  projects.length > 0 ? (
                    projects.map(row => (
                      <ProjectListRow
                        key={`row-${row.locator}`}
                        onSelect={onProjectSelected}
                        project={row}
                      />
                    ))
                  ) : (
                    <tr className="row-knockout">
                      <td className="text-center" colSpan={4}>
                        No Projects Found.
                      </td>
                    </tr>
                  )
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}
